import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface RegulatedGoldQuoteListItem {
  id: string;
  metalCode: string;
  bidPrice: number;
  askPrice: number;
  currencyCode: string;
  validUntilUtc: string;
}

export interface RegulatedGoldQuoteResponse {
  catalogQuoteId: string;
  priceLockId?: string | null;
  metalCode: string;
  bidPrice: number;
  askPrice: number;
  currencyCode: string;
  validUntilUtc: string;
}

export interface RegulatedGoldOrderListItem {
  id: string;
  side: string;
  status: string;
  quantityGrams: number;
}

export interface RegulatedGoldOrderDetail extends RegulatedGoldOrderListItem {
  limitPrice: number;
  quoteId?: string | null;
  assays: { id: string; purityPercent: number; weightGrams: number; certificateNumber: string }[];
  priceLocks: { id: string; catalogQuoteId?: string | null; metalCode: string; lockedPrice: number; expiresAtUtc: string; isActive: boolean }[];
}

export interface RegulatedBrokerageAccountListItem {
  id: string;
  accountNumber: string;
  customerId?: string | null;
  suitabilityApproved: boolean;
}

export interface RegulatedBrokerageOrderListItem {
  id: string;
  accountId: string;
  symbol: string;
  status: string;
}

export interface RegulatedBrokerageOrderDetail extends RegulatedBrokerageOrderListItem {
  quantity: number;
  executions: { id: string; executedPrice: number; executedQuantity: number; executedAtUtc: string }[];
  marginSnapshots: { id: string; equity: number; usedMargin: number; availableMargin: number; snapshotAtUtc: string }[];
}

export interface RegulatedInsuranceQuoteRecord {
  id: string;
  customerAccountId?: string | null;
  productCode: string;
  premiumAmount: number;
  currencyCode: string;
  validUntilUtc: string;
  status: string;
}

export interface RegulatedInsuranceProposalRecord {
  id: string;
  quoteId: string;
  proposalNumber: string;
  proposedPremium: number;
  status: string;
  catalogPolicyId?: string | null;
}

export interface RegulatedInsurancePolicyListItem {
  id: string;
  policyNumber: string;
  premiumAmount: number;
  currencyCode: string;
}

export interface RegulatedInsurancePolicyDetail extends RegulatedInsurancePolicyListItem {
  customerId?: string | null;
  effectiveFromUtc: string;
  effectiveToUtc: string;
}

export interface InsuranceHardeningSloSnapshot {
  flow: string;
  p95Ms: number;
  targetMs: number;
  targetMet: boolean;
}

export interface InsuranceDashboardReadModel {
  inForcePolicyCount: number;
  lossRatio: number;
  openClaimsCount: number;
  bindRatio: number;
}

export interface InsuranceObservabilityMetrics {
  date: string;
  quotesPerDay: number;
  bindRatio: number;
  lossRatio: number;
  openClaimAgeDays: number;
  sloSnapshots: InsuranceHardeningSloSnapshot[];
}

export interface InsuranceObservabilityTraceCoverage {
  quoteStage: boolean;
  underwritingStage: boolean;
  issueStage: boolean;
  collectionStage: boolean;
}

export interface InsuranceObservabilityAlert {
  alertCode: string;
  severity: string;
  status: string;
  message: string;
}

export interface InsuranceObservabilityStatus {
  metrics: InsuranceObservabilityMetrics;
  traceCoverage: InsuranceObservabilityTraceCoverage;
  alerts: InsuranceObservabilityAlert[];
  dashboardReadModel: InsuranceDashboardReadModel;
}

export interface InsuranceReconciliationJobStatus {
  jobCode: string;
  status: string;
  windowStart: string;
  windowEnd: string;
  details: string;
}

export interface InsuranceReconciliationWindow {
  windowStart: string;
  windowEnd: string;
  overallStatus: string;
  jobs: InsuranceReconciliationJobStatus[];
}

export interface InsuranceRunbookItem {
  code: string;
  title: string;
  path: string;
}

export interface InsurancePilotWaveStatus {
  waveCode: string;
  lineOfBusiness: string;
  channel: string;
  enabled: boolean;
}

export interface InsuranceCutoverChecklistItem {
  code: string;
  title: string;
  completed: boolean;
}

export interface InsuranceGoNoGoCriteria {
  programDodSatisfied: boolean;
  pilotP1StableFourWeeks: boolean;
  regulatorySubmitSuccessRateMet: boolean;
  ufcReconciliationZeroMaterialVariance: boolean;
}

export interface InsuranceHypercareStatus {
  startedOn: string;
  durationWeeks: number;
  currentWeek: number;
  warRoomDailyActive: boolean;
  openP1Incidents: number;
  regulatorySubmitSuccessRate: number;
  conversionRatio: number;
  lossRatio: number;
  bauHandoverCompleted: boolean;
}

export interface InsurancePilotGoLiveStatus {
  dataMigrationMode: string;
  pilotWaves: InsurancePilotWaveStatus[];
  cutoverChecklist: InsuranceCutoverChecklistItem[];
  hypercare: InsuranceHypercareStatus;
  goNoGoCriteria: InsuranceGoNoGoCriteria;
  productionGoLiveDocumented: boolean;
  hypercareClosed: boolean;
  bauHandoverCompleted: boolean;
}

export interface RegulatedFxQuoteResponse {
  catalogQuoteId: string;
  baseCurrency: string;
  quoteCurrency: string;
  rate: number;
  validUntilUtc: string;
}

export interface RegulatedFxTradeListItem {
  id: string;
  status: string;
  baseAmount: number;
  rate: number;
}

export interface RegulatedFxTradeDetail extends RegulatedFxTradeListItem {
  baseCurrency: string;
  quoteCurrency: string;
  quoteId?: string | null;
  settledQuoteAmount: number;
}

export interface RegulatedComplianceCaseRecord {
  id: string;
  subjectType: string;
  subjectId: string;
  screeningType: string;
  result: string;
  isHardStop: boolean;
  decisionNotes: string;
  createdAtUtc: string;
}

@Injectable({ providedIn: 'root' })
export class SalesRegulatedApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/reg-sales`;

  listGoldQuotes(): Observable<RegulatedGoldQuoteListItem[]> {
    return this.http.get<RegulatedGoldQuoteListItem[]>(`${this.base}/gold/quotes`);
  }

  getGoldQuote(id: string): Observable<RegulatedGoldQuoteListItem> {
    return this.http.get<RegulatedGoldQuoteListItem>(`${this.base}/gold/quotes/${id}`);
  }

  createGoldQuote(payload: {
    metalCode: string;
    bidPrice: number;
    askPrice: number;
    currencyCode?: string;
    validForMinutes?: number;
    customerAccountId?: string | null;
  }): Observable<RegulatedGoldQuoteResponse> {
    return this.http.post<RegulatedGoldQuoteResponse>(`${this.base}/gold/quotes`, payload);
  }

  listGoldOrders(): Observable<RegulatedGoldOrderListItem[]> {
    return this.http.get<RegulatedGoldOrderListItem[]>(`${this.base}/gold/orders`);
  }

  getGoldOrder(id: string): Observable<RegulatedGoldOrderDetail> {
    return this.http.get<RegulatedGoldOrderDetail>(`${this.base}/gold/orders/${id}`);
  }

  submitGoldOrder(payload: {
    quoteId?: string | null;
    priceLockId?: string | null;
    side: string;
    quantityGrams: number;
    limitPrice: number;
    customerAccountId?: string | null;
  }): Observable<RegulatedGoldOrderListItem> {
    return this.http.post<RegulatedGoldOrderListItem>(`${this.base}/gold/orders`, payload);
  }

  settleGoldOrder(id: string, settledAmount: number, currencyCode?: string): Observable<RegulatedGoldOrderListItem> {
    return this.http.post<RegulatedGoldOrderListItem>(`${this.base}/gold/orders/${id}/settle`, { settledAmount, currencyCode });
  }

  recordGoldAssay(id: string, payload: { purityPercent: number; weightGrams: number; certificateNumber: string }): Observable<unknown> {
    return this.http.post(`${this.base}/gold/orders/${id}/assays`, payload);
  }

  listBrokerageAccounts(): Observable<RegulatedBrokerageAccountListItem[]> {
    return this.http.get<RegulatedBrokerageAccountListItem[]>(`${this.base}/brokerage/accounts`);
  }

  createBrokerageAccount(payload: { accountNumber: string; customerId: string; suitabilityApproved: boolean }): Observable<RegulatedBrokerageAccountListItem> {
    return this.http.post<RegulatedBrokerageAccountListItem>(`${this.base}/brokerage/accounts`, payload);
  }

  listBrokerageOrders(): Observable<RegulatedBrokerageOrderListItem[]> {
    return this.http.get<RegulatedBrokerageOrderListItem[]>(`${this.base}/brokerage/orders`);
  }

  getBrokerageOrder(id: string): Observable<RegulatedBrokerageOrderDetail> {
    return this.http.get<RegulatedBrokerageOrderDetail>(`${this.base}/brokerage/orders/${id}`);
  }

  submitBrokerageOrder(payload: {
    accountId: string;
    symbol: string;
    side: string;
    quantity: number;
    customerAccountId?: string | null;
  }): Observable<RegulatedBrokerageOrderListItem> {
    return this.http.post<RegulatedBrokerageOrderListItem>(`${this.base}/brokerage/orders`, payload);
  }

  executeBrokerageOrder(id: string): Observable<RegulatedBrokerageOrderListItem> {
    return this.http.post<RegulatedBrokerageOrderListItem>(`${this.base}/brokerage/orders/${id}/execute-status`, {});
  }

  listInsuranceQuotes(): Observable<RegulatedInsuranceQuoteRecord[]> {
    return this.http.get<RegulatedInsuranceQuoteRecord[]>(`${this.base}/insurance/quotes`);
  }

  getInsuranceQuote(id: string): Observable<RegulatedInsuranceQuoteRecord> {
    return this.http.get<RegulatedInsuranceQuoteRecord>(`${this.base}/insurance/quotes/${id}`);
  }

  createInsuranceQuote(payload: {
    customerAccountId?: string | null;
    productCode: string;
    premiumAmount: number;
    currencyCode?: string;
    validForMinutes?: number;
  }): Observable<RegulatedInsuranceQuoteRecord> {
    return this.http.post<RegulatedInsuranceQuoteRecord>(`${this.base}/insurance/quotes`, payload);
  }

  listInsuranceProposals(quoteId: string): Observable<RegulatedInsuranceProposalRecord[]> {
    return this.http.get<RegulatedInsuranceProposalRecord[]>(`${this.base}/insurance/quotes/${quoteId}/proposals`);
  }

  createInsuranceProposal(payload: { quoteId: string; proposalNumber: string; proposedPremium: number }): Observable<RegulatedInsuranceProposalRecord> {
    return this.http.post<RegulatedInsuranceProposalRecord>(`${this.base}/insurance/proposals`, payload);
  }

  listInsurancePolicies(): Observable<RegulatedInsurancePolicyListItem[]> {
    return this.http.get<RegulatedInsurancePolicyListItem[]>(`${this.base}/insurance/policies`);
  }

  getInsurancePolicy(id: string): Observable<RegulatedInsurancePolicyDetail> {
    return this.http.get<RegulatedInsurancePolicyDetail>(`${this.base}/insurance/policies/${id}`);
  }

  getInsuranceObservabilityStatus(): Observable<InsuranceObservabilityStatus> {
    return this.http.get<InsuranceObservabilityStatus>(`${environment.apiBaseUrl}/api/v1/insurance/observability/status`);
  }

  getInsuranceReconciliationWindow(): Observable<InsuranceReconciliationWindow> {
    return this.http.get<InsuranceReconciliationWindow>(`${environment.apiBaseUrl}/api/v1/insurance/reconciliation/30-day-status`);
  }

  listInsuranceRunbooks(): Observable<InsuranceRunbookItem[]> {
    return this.http.get<InsuranceRunbookItem[]>(`${environment.apiBaseUrl}/api/v1/insurance/dr/runbooks`);
  }

  getInsurancePilotGoLiveStatus(): Observable<InsurancePilotGoLiveStatus> {
    return this.http.get<InsurancePilotGoLiveStatus>(`${environment.apiBaseUrl}/api/v1/insurance/go-live/status`);
  }

  updateInsurancePilotScope(payload: {
    dataMigrationMode: string;
    pilotWaves: InsurancePilotWaveStatus[];
  }): Observable<InsurancePilotGoLiveStatus> {
    return this.http.put<InsurancePilotGoLiveStatus>(`${environment.apiBaseUrl}/api/v1/insurance/go-live/pilot-scope`, payload);
  }

  updateInsuranceCutoverChecklist(payload: {
    items: InsuranceCutoverChecklistItem[];
  }): Observable<InsurancePilotGoLiveStatus> {
    return this.http.put<InsurancePilotGoLiveStatus>(`${environment.apiBaseUrl}/api/v1/insurance/go-live/cutover-checklist`, payload);
  }

  updateInsuranceHypercareStatus(payload: {
    startedOn?: string | null;
    warRoomDailyActive: boolean;
    openP1Incidents: number;
    bauHandoverCompleted: boolean;
  }): Observable<InsurancePilotGoLiveStatus> {
    return this.http.put<InsurancePilotGoLiveStatus>(`${environment.apiBaseUrl}/api/v1/insurance/go-live/hypercare`, payload);
  }

  documentInsuranceProductionGoLive(): Observable<InsurancePilotGoLiveStatus> {
    return this.http.post<InsurancePilotGoLiveStatus>(`${environment.apiBaseUrl}/api/v1/insurance/go-live/document-production`, {});
  }

  completeInsuranceBauHandover(): Observable<InsurancePilotGoLiveStatus> {
    return this.http.post<InsurancePilotGoLiveStatus>(`${environment.apiBaseUrl}/api/v1/insurance/go-live/handover-bau`, {});
  }

  issueInsurancePolicy(payload: {
    proposalId?: string | null;
    customerAccountId?: string | null;
    policyNumber: string;
    premiumAmount: number;
    currencyCode?: string;
    effectiveFromUtc: string;
    effectiveToUtc: string;
  }): Observable<RegulatedInsurancePolicyListItem> {
    return this.http.post<RegulatedInsurancePolicyListItem>(`${this.base}/insurance/policies`, payload);
  }

  createFxQuote(payload: { baseCurrency: string; quoteCurrency: string; rate: number; validForMinutes?: number }): Observable<RegulatedFxQuoteResponse> {
    return this.http.post<RegulatedFxQuoteResponse>(`${this.base}/fx/quotes`, payload);
  }

  listFxTrades(): Observable<RegulatedFxTradeListItem[]> {
    return this.http.get<RegulatedFxTradeListItem[]>(`${this.base}/fx/trades`);
  }

  getFxTrade(id: string): Observable<RegulatedFxTradeDetail> {
    return this.http.get<RegulatedFxTradeDetail>(`${this.base}/fx/trades/${id}`);
  }

  executeFxTrade(payload: {
    quoteId?: string | null;
    baseCurrency: string;
    quoteCurrency: string;
    baseAmount: number;
    rate: number;
    customerAccountId?: string | null;
  }): Observable<RegulatedFxTradeListItem> {
    return this.http.post<RegulatedFxTradeListItem>(`${this.base}/fx/trades`, payload);
  }

  listComplianceCases(): Observable<RegulatedComplianceCaseRecord[]> {
    return this.http.get<RegulatedComplianceCaseRecord[]>(`${this.base}/compliance/cases`);
  }

  getComplianceCase(id: string): Observable<RegulatedComplianceCaseRecord> {
    return this.http.get<RegulatedComplianceCaseRecord>(`${this.base}/compliance/cases/${id}`);
  }

  runComplianceScreen(payload: {
    subjectType: string;
    subjectId: string;
    screeningType: string;
    simulateHardStop: boolean;
  }): Observable<unknown> {
    return this.http.post(`${this.base}/compliance/screen`, payload);
  }

  recordSuitability(payload: {
    customerAccountId: string;
    productCategory: string;
    outcome: string;
    notes?: string;
  }): Observable<unknown> {
    return this.http.post(`${this.base}/compliance/suitability`, payload);
  }
}
